import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Spinner } from '@bbpos/ui';
import { KdsLoginPage } from '@/pages/KdsLoginPage';
import { useAuthStore } from '@/stores/use-auth-store';

// ─── Role levels (matching backend RBAC hierarchy) ──────────────────────────

const ROLE_LEVELS: Record<string, number> = {
  SUPER_ADMIN: 100,
  ADMIN: 80,
  MODERATOR: 60,
  OWNER: 50,
  MANAGER: 30,
  CAPTAIN: 7,
  CASHIER: 5,
};

const MIN_KDS_ROLE_LEVEL = ROLE_LEVELS.CASHIER; // 5

// ─── Lazy-loaded pages ──────────────────────────────────────────────────────

const KdsMainPage = lazy(() =>
  import('@/pages/KdsMainPage').then((m) => ({ default: m.KdsMainPage })),
);
const KdsStationPage = lazy(() =>
  import('@/pages/KdsStationPage').then((m) => ({ default: m.KdsStationPage })),
);

// ─── Loading fallback ───────────────────────────────────────────────────────

const LazyFallback = (
  <div className="flex items-center justify-center min-h-screen bg-neutral-950">
    <Spinner variant="page" size="md" label="Loading..." />
  </div>
);

// ─── 403 Forbidden Page ─────────────────────────────────────────────────────

function ForbiddenPage() {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-neutral-950 p-4">
      <div className="w-16 h-16 rounded-2xl bg-danger-500/20 flex items-center justify-center mb-4">
        <svg className="h-8 w-8 text-danger-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0110 0v4" />
        </svg>
      </div>
      <h1 className="text-xl font-bold text-white mb-2">Access Denied</h1>
      <p className="text-neutral-400 text-sm text-center max-w-sm">
        You don&apos;t have permission to access the Kitchen Display System. Contact your administrator.
      </p>
    </div>
  );
}

// ─── 404 Not Found Page ─────────────────────────────────────────────────────

function NotFoundPage() {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-neutral-950 p-4">
      <div className="w-16 h-16 rounded-2xl bg-neutral-800 flex items-center justify-center mb-4">
        <svg className="h-8 w-8 text-neutral-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      </div>
      <h1 className="text-xl font-bold text-white mb-2">Page Not Found</h1>
      <p className="text-neutral-400 text-sm text-center max-w-sm">
        The page you&apos;re looking for doesn&apos;t exist or has been moved.
      </p>
    </div>
  );
}

// ─── Protected Route Guard ──────────────────────────────────────────────────

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isHydrated = useAuthStore((s) => s.isHydrated);

  if (!isHydrated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-neutral-950">
        <Spinner variant="page" size="md" label="Loading..." />
      </div>
    );
  }

  // §5.1 Step 1-2: No auth → redirect to login with return URL
  if (!isAuthenticated) {
    const returnUrl = window.location.pathname + window.location.search;
    return <Navigate to={`/login?redirect=${encodeURIComponent(returnUrl)}`} replace />;
  }

  // §5.1 Step 4: Role check — minimum CASHIER (5) for KDS
  if (user) {
    const roleLevel = ROLE_LEVELS[user.role?.toUpperCase()] ?? 0;
    if (roleLevel < MIN_KDS_ROLE_LEVEL) {
      return <ForbiddenPage />;
    }
  }

  // §5.1 Step 5: Feature flag check (ENABLE_KDS)
  // Note: Feature flag validation is primarily server-side. The KDS app itself
  // is only deployed when ENABLE_KDS is true. If the API returns 404 for KDS
  // endpoints, the error boundary will catch it. Client-side flag checks
  // would require an additional API call to fetch tenant config — deferred to
  // the tenant context interceptor which returns 404 for disabled features.

  // §5.1 Step 6: Vertical mismatch (non-RESTAURANT tenant → 404)
  // Same as above — server enforces vertical scope. KDS endpoints only exist
  // for RESTAURANT tenants. A 404 from the server triggers the error state.

  return <>{children}</>;
}

// ─── Root redirect ──────────────────────────────────────────────────────────

function RootRedirect() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isHydrated = useAuthStore((s) => s.isHydrated);

  if (!isHydrated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-neutral-950">
        <Spinner variant="page" size="md" label="Loading..." />
      </div>
    );
  }

  return isAuthenticated ? (
    <Navigate to="/kds" replace />
  ) : (
    <Navigate to="/login" replace />
  );
}

// ─── App Router ─────────────────────────────────────────────────────────────

export function AppRouter() {
  const hydrate = useAuthStore((s) => s.hydrate);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<KdsLoginPage />} />
      <Route
        path="/kds"
        element={
          <ProtectedRoute>
            <Suspense fallback={LazyFallback}>
              <KdsMainPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/kds/station/:stationId"
        element={
          <ProtectedRoute>
            <Suspense fallback={LazyFallback}>
              <KdsStationPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
